import { useState } from 'react';
import { Navigate, Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../../api/errors';
import { queryKeys } from '../../../api/query-keys';
import { useAuth } from '../../../auth/AuthProvider';
import { permissions } from '../../../auth/permissions';
import { useMachines } from '../../machines/api/useMachines';
import { createInvestigation } from '../api/investigations.api';
import { useInvestigations } from '../api/useInvestigations';
import { IncrementalListFooter } from '../../../components/ui/IncrementalListFooter';
import { useIncrementalItems } from '../../../components/ui/useIncrementalItems';
import { STANDARD_LIST_BATCH_SIZE } from '../../../components/ui/list-batch-sizes';
import { StartInvestigationDialog } from '../components/StartInvestigationDialog';
import { investigationStatusLabel } from '../types/investigation';

export function InvestigationsPage() {
  const { machineId = '' } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const machines = useMachines();
  const investigations = useInvestigations(machineId, auth.can(permissions.investigationRead));
  const machine = machines.data?.machines.find((item) => item.id === machineId);
  const scope = `${auth.activeCustomerId}:${machineId}`;
  const [searchState, setSearchState] = useState({ scope, value: '' });
  const search = searchState.scope === scope ? searchState.value : '';
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const allInvestigations = investigations.data?.investigations ?? [];
  const matchingInvestigations = normalizedSearch
    ? allInvestigations.filter((item) => [item.title, item.objective, item.status, investigationStatusLabel(item.status)]
      .some((field) => field.toLocaleLowerCase().includes(normalizedSearch)))
    : allInvestigations;
  const pager = useIncrementalItems(matchingInvestigations, STANDARD_LIST_BATCH_SIZE, `${scope}:${normalizedSearch}`);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!auth.can(permissions.investigationRead)) return <Navigate to={`/machines/${machineId}`} replace />;

  async function create(title: string, objective: string) {
    setBusy(true); setError(null);
    try {
      const result = await createInvestigation(machineId, title, objective);
      await queryClient.invalidateQueries({ queryKey: queryKeys.investigations(auth.activeCustomerId, machineId) });
      navigate(`/machines/${machineId}/investigations/${result.investigation.investigation_id}`);
    } catch (value) { setError(message(value)); }
    finally { setBusy(false); }
  }

  return <section className="r42-investigations-page">
    <header className="r42-form-heading">
      <div><span className="r42-eyebrow">Machine workspace</span><h1>Investigations</h1><p>Work through one machine problem with your team until you know what to do next.</p></div>
      {auth.can(permissions.investigationCreate) ? <button className="btn r42-btn-primary" onClick={() => setShowCreate(true)}><i className="bi bi-plus-lg" aria-hidden="true" /> Start investigation</button> : null}
    </header>

    {allInvestigations.length > 0 ? (
      <div className="r42-list-toolbar">
        <label className="r42-search">
          <i className="bi bi-search" aria-hidden="true" />
          <span className="visually-hidden">Search investigations</span>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearchState({ scope, value: event.target.value })}
            placeholder="Search investigations..."
          />
        </label>
      </div>
    ) : null}
    {investigations.isPending ? <div className="r42-inline-state" role="status">Loading investigations…</div> : investigations.isError ? <div className="r42-inline-state r42-inline-state--error"><span>Route42 couldn't load investigations.</span><button className="btn btn-sm btn-outline-secondary" onClick={() => void investigations.refetch()}>Try again</button></div> : allInvestigations.length === 0 ? <div className="r42-investigation-empty"><i className="bi bi-search" aria-hidden="true" /><h2>No investigations yet</h2><p>Start an Investigation when a machine problem is important, unresolved, or needs follow-through across people, shifts, or new evidence.</p><div className="r42-investigation-empty-actions">{auth.can(permissions.investigationCreate) ? <button className="btn r42-btn-primary" onClick={() => setShowCreate(true)}>Start investigation</button> : null}<Link className="btn btn-outline-secondary" to="/learn/investigations">See an Investigation in action</Link></div></div> : matchingInvestigations.length === 0 ? (
      <div className="r42-inline-state" role="status">
        <span>No investigations match your search.</span>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setSearchState({ scope, value: '' })}>Clear search</button>
      </div>
    ) : <div className="r42-investigation-list">
      {pager.visible.map((item) => <Link key={item.investigation_id} className="r42-investigation-row" to={`/machines/${machineId}/investigations/${item.investigation_id}`}>
        <span className="r42-investigation-row-icon"><i className="bi bi-search" aria-hidden="true" /></span>
        <span className="r42-investigation-row-body"><strong>{item.title}</strong><small>{item.objective}</small><span>{machine?.name ?? 'Machine'} • Updated {formatDate(item.updated_at ?? item.created_at)}</span></span>
        <span className={`r42-investigation-status r42-investigation-status--${item.status.toLowerCase()}`}>{investigationStatusLabel(item.status)}</span>
        <i className="bi bi-chevron-right r42-document-chevron" aria-hidden="true" />
      </Link>)}
    </div>}
    <IncrementalListFooter label="investigations" loaded={pager.loaded} total={pager.total} hasMore={pager.hasMore} onLoadMore={pager.loadMore} />

    {showCreate ? <StartInvestigationDialog busy={busy} error={error} onClose={() => { if (!busy) { setShowCreate(false); setError(null); } }} onSubmit={create} /> : null}
  </section>;
}

function formatDate(value?: string | null) { if (!value) return 'recently'; const date = new Date(value); return Number.isNaN(date.getTime()) ? 'recently' : new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date); }
function message(value: unknown) { return value instanceof ApiError ? value.message : 'Route42 could not start the investigation. Please try again.'; }
